import React from 'react';
import { Checkbox } from '@cotera/client/v0/components/ui/checkbox';
import { cn } from '@cotera/client/v0/lib/utils';
import { RowActionsMenu } from './row-actions-menu';
import type { ItemAction } from './resource-list-view-types';

export type VirtualGridCardProps<T> = {
  item: T;
  index: number;
  optionId: string;
  renderCard: (item: T) => React.ReactNode;
  actions: ItemAction[];
  onOpenActionMenu?: (item: T) => void;
  isSelected: boolean;
  isFocused: boolean;
  /** A selection exists, so checkboxes stay pinned across every card. */
  multiSelectActive: boolean;
  isDraggable?: boolean;
  isDragging?: boolean;
  isDropTarget?: boolean;
  onDragStartRow?: (
    index: number,
    event: React.DragEvent<HTMLDivElement>
  ) => void;
  onDragOverRow?: (
    index: number,
    event: React.DragEvent<HTMLDivElement>
  ) => void;
  onDropRow?: (index: number, event: React.DragEvent<HTMLDivElement>) => void;
  onDragEndRow?: () => void;
  onRowClick: (
    item: T,
    index: number,
    event: React.MouseEvent<HTMLDivElement>
  ) => void;
  onToggleSelect?: (index: number) => void;
  onShiftSelect?: (index: number) => void;
  onMouseEnterRow: (index: number) => void;
  onClickItem?: (item: T) => void;
};

/**
 * One card in grid mode. Deliberately mirrors `VirtualListItem`'s interaction
 * contract — same index-based selection, drag and action-menu handlers — so
 * switching view modes changes only the presentation, never the behaviour.
 */
export function VirtualGridCard<T>({
  item,
  index,
  optionId,
  renderCard,
  actions,
  onOpenActionMenu,
  isSelected,
  isFocused,
  multiSelectActive,
  isDraggable = false,
  isDragging = false,
  isDropTarget = false,
  onDragStartRow,
  onDragOverRow,
  onDropRow,
  onDragEndRow,
  onRowClick,
  onToggleSelect,
  onShiftSelect,
  onMouseEnterRow,
  onClickItem,
}: VirtualGridCardProps<T>): React.ReactElement {
  // Controls stay hidden until hover/focus (or until a selection makes them
  // permanent), and are reachable by keyboard via focus-within.
  const revealClass =
    multiSelectActive || isSelected
      ? 'opacity-100'
      : cn(
          'opacity-0 pointer-events-none transition-opacity',
          'group-hover/card:opacity-100 group-hover/card:pointer-events-auto',
          'focus-within:opacity-100 focus-within:pointer-events-auto',
          'has-[:focus-visible]:opacity-100 has-[:focus-visible]:pointer-events-auto'
        );

  return (
    <div
      id={optionId}
      role="gridcell"
      aria-selected={isSelected}
      data-selected={isSelected ? 'true' : undefined}
      data-focused={isFocused ? 'true' : undefined}
      data-drop-target={isDropTarget ? 'true' : undefined}
      data-index={index}
      draggable={isDraggable}
      style={{ opacity: isDragging ? 0.4 : undefined }}
      className={cn(
        // No `justify-between`: the card body claims the slack via `flex-1`, so
        // the footer sits on the bottom edge without a ragged mid-card gap.
        'group/card relative flex flex-col rounded-lg border p-3',
        'transition-colors duration-150 h-full min-w-0',
        'border-border/60 bg-card hover:bg-secondary',
        isSelected && 'border-primary/40 bg-secondary',
        isFocused && 'ring-2 ring-ring',
        isDropTarget && '!bg-primary/15 border-primary/40',
        onClickItem !== undefined && 'cursor-pointer',
        isDraggable && 'cursor-grab active:cursor-grabbing'
      )}
      onMouseDown={(e) => {
        if (e.shiftKey) {
          e.preventDefault();
        }
      }}
      onClick={(e) => onRowClick(item, index, e)}
      onMouseEnter={() => onMouseEnterRow(index)}
      onDragStart={
        onDragStartRow !== undefined
          ? (e) => onDragStartRow(index, e)
          : undefined
      }
      onDragOver={
        onDragOverRow !== undefined ? (e) => onDragOverRow(index, e) : undefined
      }
      onDrop={onDropRow !== undefined ? (e) => onDropRow(index, e) : undefined}
      onDragEnd={onDragEndRow}
    >
      <span
        className={cn(
          'absolute right-2 top-2 z-10 flex items-center gap-1',
          revealClass,
          'has-[button[data-state=open]]:opacity-100 has-[button[data-state=open]]:pointer-events-auto'
        )}
      >
        {/* The list rows' menu, with the same trigger — a card used to draw a
            larger one, which left the Library's two view modes disagreeing about
            the size of the same control. This was a fourth hand-rolled copy of
            `RowActionsMenu` until the icons landed: it silently dropped every
            `submenu` action (a grid card could never show the agent emoji
            picker) and it was the copy that would have drifted next. */}
        <RowActionsMenu
          actions={actions}
          onOpen={() => onOpenActionMenu?.(item)}
          label="Item actions"
          contentClassName="w-40"
        />
        <Checkbox
          checked={isSelected}
          aria-label={isSelected ? 'Deselect item' : 'Select item'}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (e.shiftKey && onShiftSelect !== undefined) {
              onShiftSelect(index);
            } else {
              onToggleSelect?.(index);
            }
          }}
          onCheckedChange={() => onToggleSelect?.(index)}
        />
      </span>
      {renderCard(item)}
    </div>
  );
}
